import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { AiOutlineWarning, AiOutlineSearch } from 'react-icons/ai';
import SearchBar from './utils/SearchBar';
import CategoryFilter from './utils/CategoryFilter';
import PriceFilter from './utils/PriceFilter';
import ProductCard from './utils/ProductCard';
import { api } from '../api';
import logger from '../utils/logger';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sortBy, setSortBy] = useState('popular');
  
  // Pagination state
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // Auto-hide filter state
  const [showFilters, setShowFilters] = useState(true);
  
  // Refs for intersection observer
  const sentinelRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  const PRODUCTS_PER_PAGE = 12; // Load 12 products at a time

  // Initial fetch to get all products (once)
  useEffect(() => {
    const fetchAllProducts = async () => {
      if (initialLoadDone.current) return;
      initialLoadDone.current = true;

      try {
        setLoading(true);
        setError(null);
        logger.log('📦 Fetching products from API...');
        
        const response = await api.get('/aastrog_cart/products', {
          params: {
            page: 1,
            pageSize: 500, // Fetch larger batch to reduce API calls
            active: true
          }
        });

        if (response.data && response.data.success && response.data.data) {
          // Transform API response to component format
          const transformedProducts = response.data.data.map((product) => ({
            id: product.ProductId,
            name: product.ProductName,
            category: product.CategoryName || 'uncategorized',
            price: product.Price,
            rating: 4.5 + Math.random() * 0.4,
            reviews: Math.floor(Math.random() * 500) + 50,
            image: product.ImageUrl || 'https://via.placeholder.com/280x200?text=Product',
            badge: product.IsFeatured ? 'Featured' : null,
            desc: product.ProductDescription,
            sku: product.SKU,
            stockQty: product.StockQty,
            discountValue: product.DiscountValue || 0,
            discountName: product.DiscountName || null
          }));

          setAllProducts(transformedProducts);
          setTotalProducts(transformedProducts.length);
          
          // Extract unique categories from products
          const categories = [...new Set(transformedProducts.map(p => p.category))].sort();
          setAvailableCategories(categories);
          
          logger.log(`✅ Loaded ${transformedProducts.length} total products with ${categories.length} categories`);
        } else {
          setAllProducts([]);
          logger.warn('⚠️ API returned empty or invalid data');
        }
      } catch (err) {
        logger.error('❌ Failed to fetch products:', err);
        setError(err.message || 'Failed to load products');
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllProducts();
  }, []);

  // Auto-hide filter on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 968) {
        setShowFilters(false);
      } else {
        setShowFilters(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter products based on search, category, and price
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = allProducts.filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === 'all' || product.category.toLowerCase() === selectedCategory.toLowerCase();
      const matchesPrice =
        product.price >= priceRange[0] && product.price <= priceRange[1];
      // Filter for reviews 4★ and above
      const matchesRating = product.rating >= 4;

      return matchesSearch && matchesCategory && matchesPrice && matchesRating;
    });

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        filtered.sort((a, b) => b.id - a.id);
        break;
      case 'popular':
      default:
        filtered.sort((a, b) => b.reviews - a.reviews);
        break;
    }

    return filtered;
  }, [searchTerm, selectedCategory, priceRange, sortBy, allProducts]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setDisplayedProducts([]);
    setHasMore(true);
  }, [searchTerm, selectedCategory, priceRange, sortBy]);

  // Load more products (pagination logic)
  const loadMoreProducts = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    
    // Simulate API delay for smooth UX
    setTimeout(() => {
      const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
      const endIndex = startIndex + PRODUCTS_PER_PAGE;
      const newProducts = filteredAndSortedProducts.slice(startIndex, endIndex);

      setDisplayedProducts((prev) => [...prev, ...newProducts]);
      
      const totalLoaded = startIndex + newProducts.length;
      if (totalLoaded >= filteredAndSortedProducts.length) {
        setHasMore(false);
        logger.log(`All ${filteredAndSortedProducts.length} products loaded!`);
      } else {
        logger.log(`Loaded ${newProducts.length} more products (${totalLoaded}/${filteredAndSortedProducts.length})`);
      }

      setCurrentPage((prev) => prev + 1);
      setLoadingMore(false);
    }, 300); // 300ms delay for smooth loading animation
  }, [currentPage, filteredAndSortedProducts, hasMore, loadingMore]);

  // Initial load - load first page of products
  useEffect(() => {
    if (filteredAndSortedProducts.length > 0 && displayedProducts.length === 0) {
      const firstPageProducts = filteredAndSortedProducts.slice(0, PRODUCTS_PER_PAGE);
      setDisplayedProducts(firstPageProducts);
      
      if (filteredAndSortedProducts.length <= PRODUCTS_PER_PAGE) {
        setHasMore(false);
      }
      
      setCurrentPage(2);
    }
  }, [filteredAndSortedProducts]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          logger.log('Loading more products...');
          loadMoreProducts();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before reaching bottom
        threshold: 0.1
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [loadMoreProducts, hasMore, loadingMore, loading]);

  return (
    <div className="products-container">
      {/* Header */}
      <div className="products-header">
        <h1>Our Products.</h1>
        <p>Discover our comprehensive astrological services and readings</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="products-loading">
          <div className="loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="products-error">
          <div className="error-icon"><AiOutlineWarning size={48} /></div>
          <h3>Failed to Load Products</h3>
          <p>{error}</p>
          <button
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Main Content (only show if not loading) */}
      {!loading && (
        <>
          {/* Filters and Products Layout */}
          <div className="products-layout">
            {/* Sidebar Filters - auto-hide */}
            {showFilters && (
            <aside className="products-sidebar">
              <div className="filters-section">
                <h3 className="filter-title">Filters</h3>

                {/* Category Filter */}
                <CategoryFilter
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  availableCategories={availableCategories}
                />

                {/* Price Filter */}
                <PriceFilter priceRange={priceRange} setPriceRange={setPriceRange} />

                {/* Clear Filters */}
                <button
                  className="clear-filters-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setPriceRange([0, 10000]);
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            </aside>
            )}

            {/* Main Content */}
            <main className="products-main">
              {/* Sort and Results Info */}
              <div className="products-toolbar">
                {/* <div className="results-info">
                  <p>Showing {displayedProducts.length} of {filteredAndSortedProducts.length} results</p>
                </div> */}

                <div className="search-bar-wrapper">
                  <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>

                <div className="filter-toggle-container toolbar-filter-toggle">
                  <button
                    className="filter-toggle-btn"
                    onClick={() => setShowFilters(!showFilters)}
                    title={showFilters ? 'Hide filters' : 'Show filters'}
                  >
                    {showFilters ? '✕ Hide Filters' : '⚙️ Show Filters'}
                  </button>
                </div>

                <div className="sort-controls">
                  <label htmlFor="sort-select">Sort by:</label>
                  <select
                    id="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="popular">Most Popular</option>
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                </div>
              </div>

              {/* Products Grid - Infinite Scroll */}
              {filteredAndSortedProducts.length > 0 ? (
                <>
                  <div className="products-grid">
                    {displayedProducts.map((product, idx) => (
                      <ProductCard key={`${product.id}-${idx}`} product={product} />
                    ))}
                  </div>

                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <div className="products-loading-more">
                      <div className="mini-spinner"></div>
                      <p>Loading more products...</p>
                    </div>
                  )}

                  {/* No More Products Indicator */}
                  {!hasMore && displayedProducts.length > 0 && (
                    <div className="products-end-message">
                      <p>All {filteredAndSortedProducts.length} products loaded!</p>
                    </div>
                  )}

                  {/* Sentinel element for infinite scroll trigger */}
                  <div ref={sentinelRef} className="infinite-scroll-sentinel"></div>
                </>
              ) : (
                <div className="no-products">
                  <div className="no-products-icon"><AiOutlineSearch size={48} /></div>
                  <h3>No Products Found</h3>
                  <p>Try adjusting your filters or search terms</p>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setPriceRange([0, 10000]);
                    }}
                  >
                    View All Products
                  </button>
                </div>
              )}
            </main>
          </div>
        </>
      )}
    </div>
  );
};

export default Products;
 